import { useEffect } from 'react';

/**
 * StructuredData — injects SEO + AEO (Answer Engine Optimization) JSON-LD
 * schema into the document head. Powers rich results in Google and answer
 * surfaces in ChatGPT, Gemini, Perplexity & Bing AI.
 */
const SITE_URL = 'https://bogin.tech';

const schemas = [
    {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        '@id': `${SITE_URL}/#organization`,
        name: 'Bogin Tech',
        url: SITE_URL,
        logo: `${SITE_URL}/BoginTech_Logo192.png`,
        description:
            'Bogin Tech is a custom software development company building enterprise web apps, mobile apps, AI/ML automation, cloud platforms, and blockchain solutions for startups and Fortune 500 companies.',
        foundingDate: '2020',
        slogan: 'Architecting Intelligent Business Futures',
        sameAs: [
            'https://www.linkedin.com/company/bogintech',
            'https://twitter.com/bogintech',
        ],
        aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: '4.9',
            reviewCount: '500',
            bestRating: '5',
        },
    },
    {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        '@id': `${SITE_URL}/#website`,
        url: SITE_URL,
        name: 'Bogin Tech',
        publisher: { '@id': `${SITE_URL}/#organization` },
        potentialAction: {
            '@type': 'SearchAction',
            target: `${SITE_URL}/search?q={search_term_string}`,
            'query-input': 'required name=search_term_string',
        },
    },
    {
        '@context': 'https://schema.org',
        '@type': 'ProfessionalService',
        name: 'Bogin Tech — Custom Software Development Services',
        url: SITE_URL,
        image: `${SITE_URL}/BoginTech_Logo192.png`,
        priceRange: '$$',
        description:
            'Enterprise software development, mobile app development, AI/ML integration, cloud & DevOps, blockchain, and IoT engineering services delivering MVP to production in 90 days.',
        areaServed: 'Worldwide',
        hasOfferCatalog: {
            '@type': 'OfferCatalog',
            name: 'Technology Services',
            itemListElement: [
                'Custom Software Development',
                'Web Application Development',
                'Mobile App Development',
                'AI/ML & GPT Integration',
                'Cloud & DevOps Engineering',
                'Blockchain Development',
            ].map((service) => ({
                '@type': 'Offer',
                itemOffered: { '@type': 'Service', name: service },
            })),
        },
    },
    {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
            {
                '@type': 'Question',
                name: 'What is your approach to enterprise digital transformation?',
                acceptedAnswer: {
                    '@type': 'Answer',
                    text:
                        'Our 360° transformation framework delivers measurable results within 6-9 months through cloud-native architecture design, AI/ML integration roadmaps, legacy system modernization strategies, and continuous security integration.',
                },
            },
            {
                '@type': 'Question',
                name: 'How do you ensure solution scalability?',
                acceptedAnswer: {
                    '@type': 'Answer',
                    text:
                        'We build for enterprise growth using microservices-based design, auto-scaling cloud infrastructure, containerized deployment pipelines, and multi-cloud redundancy planning.',
                },
            },
            {
                '@type': 'Question',
                name: 'What security frameworks do you implement?',
                acceptedAnswer: {
                    '@type': 'Answer',
                    text:
                        'We deliver enterprise-grade protection through Zero Trust architecture design, SOC 2 Type II compliance, real-time threat detection systems, and end-to-end data encryption.',
                },
            },
            {
                '@type': 'Question',
                name: 'How long does it take to build custom software with Bogin Tech?',
                acceptedAnswer: {
                    '@type': 'Answer',
                    text:
                        'Bogin Tech delivers MVP to production in 90 days or less using a proven agile methodology with two-week sprints, CI/CD automation, and continuous client demos.',
                },
            },
            {
                '@type': 'Question',
                name: 'What ongoing support does Bogin Tech provide?',
                acceptedAnswer: {
                    '@type': 'Answer',
                    text:
                        'We provide 24/7 enterprise support including managed cloud services, predictive maintenance AI, SLA-backed performance guarantees, and quarterly optimization reviews.',
                },
            },
        ],
    },
];

const StructuredData = () => {
    useEffect(() => {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.id = 'bt-home-structured-data';
        script.text = JSON.stringify(schemas);
        document.head.appendChild(script);

        return () => {
            const existing = document.getElementById('bt-home-structured-data');
            if (existing) existing.remove();
        };
    }, []);

    return null;
};

export default StructuredData;
